interface ImageFile extends File {
  relativePath: string;
}

interface ImageInfo {
  path: string;
  size: [number, number, number];
}

interface Album {
  id: string;
  name: string;
  count: number;
  images: ImageInfo[];
}

interface PngDimensions {
  width: number;
  height: number;
  type: "png";
  orientation?: number;
}

interface GifDimensions {
  width: number;
  height: number;
  type: "gif";
  orientation?: number;
}

interface WebpDimensions {
  width: number;
  height: number;
  type: "webp";
  orientation?: number;
}

interface JpegDimensions {
  width: number;
  height: number;
  type: "jpeg";
  orientation: number | null;
}

type ImageDimensions =
  | PngDimensions
  | GifDimensions
  | WebpDimensions
  | JpegDimensions;

const allImages: ImageFile[] = [];
const allAlbums: Album[] = [];

// Augment global window interface
declare global {
  interface Window {
    exifr: {
      parse(file: File, options: any): Promise<any>;
    };
    pma_js_image_dimensions(
      filename: string
    ): Promise<[number, number, number]>;
    pma_js_pick_images(): Promise<void>;
    pma_js_get_images(): string[];
    pma_js_get_albums(): Album[];
    pma_js_get_image_data(photoId: string): Promise<Uint8Array>;
    pma_js_resize_image(jsonArg: string): Promise<string>;
    pma_js_resized_image_bytes(tmpKey: string): Uint8Array;
    pma_js_p_tmp_images: { key: string; bytes: Uint8Array }[];
    pma_js_p_pick_images_has_run: boolean;
    pmaFlSmartphoneSelectionServiceUpdateAlbums(albums: Album[]): void;
  }
}

window.pma_js_image_dimensions = async (filename: string) => {
  const file = allImages.find((it) => it.relativePath + it.name === filename);
  if (!file) {
    throw new Error(`Couldn't get dimensions for image ${filename}`);
  }
  const blob = file.slice(0, 16384);
  const buffer = new Uint8Array(await blob.arrayBuffer());

  let dimensions: ImageDimensions;

  if (isPng(buffer)) {
    dimensions = parsePng(buffer);
  } else if (isJpeg(buffer)) {
    const exifData = await window.exifr.parse(file, {
      translateValues: false,
      exif: ["Orientation", "DateTimeOriginal", "ImageWidth", "ImageHeight"],
    });
    const { Orientation, ImageWidth, ImageHeight } = exifData;

    const orientationToInt: Record<number, number> = { 1: 0, 3: 2, 6: 1, 8: 3 };
    const rotation = orientationToInt[Orientation] ?? 0;
    const flip = rotation % 2 !== 0;

    dimensions = {
      width: flip ? ImageHeight : ImageWidth,
      height: flip ? ImageWidth : ImageHeight,
      orientation: rotation,
      type: "jpeg",
    };
  } else if (isGif(buffer)) {
    dimensions = parseGif(buffer);
  } else if (isWebp(buffer)) {
    dimensions = parseWebp(buffer);
  } else {
    throw new Error("Unsupported image format");
  }

  return [dimensions.width, dimensions.height, dimensions.orientation ?? 0];
};

function isPng(buffer: Uint8Array): boolean {
  return (
    buffer[0] === 0x89 &&
    buffer[1] === 0x50 &&
    buffer[2] === 0x4e &&
    buffer[3] === 0x47
  );
}

function isJpeg(buffer: Uint8Array): boolean {
  return buffer[0] === 0xff && buffer[1] === 0xd8;
}

function isGif(buffer: Uint8Array): boolean {
  return buffer[0] === 0x47 && buffer[1] === 0x49 && buffer[2] === 0x46;
}

function isWebp(buffer: Uint8Array): boolean {
  return (
    buffer[0] === 0x52 &&
    buffer[1] === 0x49 &&
    buffer[2] === 0x46 &&
    buffer[3] === 0x46 &&
    buffer[8] === 0x57 &&
    buffer[9] === 0x45 &&
    buffer[10] === 0x42 &&
    buffer[11] === 0x50
  );
}

function parsePng(buffer: Uint8Array): PngDimensions {
  const width =
    (buffer[16] << 24) | (buffer[17] << 16) | (buffer[18] << 8) | buffer[19];
  const height =
    (buffer[20] << 24) | (buffer[21] << 16) | (buffer[22] << 8) | buffer[23];
  return { width, height, type: "png" };
}

function parseGif(buffer: Uint8Array): GifDimensions {
  const width = buffer[6] | (buffer[7] << 8);
  const height = buffer[8] | (buffer[9] << 8);
  return { width, height, type: "gif" };
}

function parseWebp(buffer: Uint8Array): WebpDimensions {
  const chunkHeader = String.fromCharCode(
    buffer[12],
    buffer[13],
    buffer[14],
    buffer[15]
  );
  if (chunkHeader === "VP8 ") {
    const width = buffer[26] | (buffer[27] << 8);
    const height = buffer[28] | (buffer[29] << 8);
    return { width, height, type: "webp" };
  } else if (chunkHeader === "VP8L") {
    const b0 = buffer[21];
    const b1 = buffer[22];
    const b2 = buffer[23];
    const b3 = buffer[24];
    const width = 1 + (((b1 & 0x3f) << 8) | b0);
    const height = 1 + (((b3 & 0xf) << 10) | (b2 << 2) | ((b1 & 0xc0) >> 6));
    return { width, height, type: "webp" };
  } else if (chunkHeader === "VP8X") {
    const width = 1 + (buffer[24] | (buffer[25] << 8) | (buffer[26] << 16));
    const height = 1 + (buffer[27] | (buffer[28] << 8) | (buffer[29] << 16));
    return { width, height, type: "webp" };
  } else {
    throw new Error("Unknown WebP format");
  }
}

function parseJpeg(buffer: Uint8Array): JpegDimensions {
  let i = 2;
  let orientation: number | null = null;

  while (i < buffer.length) {
    if (buffer[i] !== 0xff) {
      i++;
      continue;
    }
    const marker = buffer[i + 1];
    if (
      marker === 0xd8 ||
      marker === 0xd9 ||
      (marker >= 0xd0 && marker <= 0xd7)
    ) {
      i += 2;
      continue;
    }
    const length = (buffer[i + 2] << 8) + buffer[i + 3];
    if (marker === 0xe1) {
      const exifSection = buffer.subarray(i + 4, i + 2 + length);
      orientation = readExifOrientation(exifSection);
    } else if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xcc) {
      const height = (buffer[i + 5] << 8) + buffer[i + 6];
      const width = (buffer[i + 7] << 8) + buffer[i + 8];
      return { width, height, type: "jpeg", orientation };
    }
    i += 2 + length;
  }
  throw new Error("Could not find JPEG size info");
}

function readExifOrientation(buff: Uint8Array): number | null {
  if (
    buff[0] !== 0x45 ||
    buff[1] !== 0x78 ||
    buff[2] !== 0x69 ||
    buff[3] !== 0x66
  )
    return null;
  const le = buff[6] === 0x49 && buff[7] === 0x49;
  let off = 10;
  const entries = readShort(buff, off, le);
  off += 2;
  for (let i = 0; i < entries; i++) {
    const tag = readShort(buff, off + 12 * i, le);
    if (tag === 0x0112) return readShort(buff, off + 12 * i + 8, le);
  }
  return null;
}

function readShort(buff: Uint8Array, off: number, le: boolean): number {
  return le
    ? buff[off] + (buff[off + 1] << 8)
    : (buff[off] << 8) + buff[off + 1];
}

function makeId(len: number): string {
  let res = "";
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < len; i++)
    res += chars.charAt(Math.floor(Math.random() * chars.length));
  return res;
}

function readFileAsUint8Array(file: ImageFile): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

async function* getFilesRecursively(
  path: string,
  entry: any
): AsyncGenerator<ImageFile> {
  if (entry.kind === "file") {
    const file = await entry.getFile();
    (file as ImageFile).relativePath = path;
    yield file;
  } else {
    for await (const handle of entry.values()) {
      yield* getFilesRecursively(path + entry.name + "/", handle);
    }
  }
}

window.pma_js_p_pick_images_has_run = false;
window.pma_js_pick_images = async () => {
  if (window.pma_js_p_pick_images_has_run) return;
  window.pma_js_p_pick_images_has_run = true;
  const dir = await (window as any).showDirectoryPicker();
  for await (const fh of getFilesRecursively("", dir)) {
    allImages.push(fh);
  }
  for (const image of allImages) {
    const idx = allAlbums.findIndex((a) => a.id === image.relativePath);
    const path = image.relativePath + image.name;
    const info: ImageInfo = {
      path,
      size: await window.pma_js_image_dimensions(path),
    };
    if (idx === -1) {
      allAlbums.push({
        id: image.relativePath,
        name: image.relativePath,
        count: 1,
        images: [info],
      });
    } else {
      allAlbums[idx].count++;
      allAlbums[idx].images.push(info);
    }
  }
  window.pmaFlSmartphoneSelectionServiceUpdateAlbums(allAlbums);
};

window.pma_js_get_images = () =>
  allAlbums.flatMap((a) => a.images.map((i) => i.path));
window.pma_js_get_albums = () => allAlbums;

window.pma_js_get_image_data = (photoId: string) => {
  const img = allImages.find((it) => it.relativePath + it.name === photoId);
  if (!img) throw new Error(`Couldn't load image ${photoId}`);
  return readFileAsUint8Array(img);
};

window.pma_js_p_tmp_images = [];

async function resizeAndSaveToJpegBytes(
  imgEl: HTMLImageElement,
  tw: number,
  th: number
): Promise<Uint8Array> {
  const c = document.createElement("canvas");
  c.width = tw;
  c.height = th;
  const ctx = c.getContext("2d");
  if (!ctx) throw Error("");
  ctx.drawImage(imgEl, 0, 0, tw, th);
  const blob = (await new Promise<Blob | null>((res) =>
    c.toBlob(res, "image/jpeg", 0.95)
  ))!;
  return new Uint8Array(await blob.arrayBuffer());
}

async function fileToImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  return new Promise((res, rej) => {
    img.onload = () => {
      URL.revokeObjectURL(url);
      res(img);
    };
    img.onerror = rej;
    img.src = url;
  });
}

async function resizeFileToJpegBytes(
  file: File,
  tw: number,
  th: number
): Promise<Uint8Array> {
  return resizeAndSaveToJpegBytes(await fileToImage(file), tw, th);
}

window.pma_js_resize_image = async (jsonArg: string) => {
  const { in_filename } = JSON.parse(jsonArg);
  const imgFile = allImages.find(
    (it) => it.relativePath + it.name === in_filename
  );
  if (!imgFile) throw new Error(`Couldn't resize image ${in_filename}`);
  const bytes = await resizeFileToJpegBytes(imgFile, 1000, 500);
  let key: string | null = null;
  for (let i = 0; i < 10; i++) {
    const k = makeId(10);
    if (!window.pma_js_p_tmp_images.find((e) => e.key === k)) {
      key = k;
      break;
    }
  }
  if (!key) throw new Error("Too many keys");
  window.pma_js_p_tmp_images.push({ key, bytes });
  return key;
};

window.pma_js_resized_image_bytes = (tmpKey: string) => {
  const ent = window.pma_js_p_tmp_images.find((e) => e.key === tmpKey);
  if (!ent) throw new Error(`Couldn't retrieve resized image ${tmpKey}`);
  return ent.bytes;
};

export {};
